/**
 * VueX Store: PLP
 * ------------------------------------------------------------------------------
 * - Legacy PLP store powered by VueX
 * - Queries Attraqt API and returns formatted products
 * - Eventual plan is to remove dependency on VueX entirely and use Pinia
 * - This file should be optimised continuously.
 *
 */
import { createStore } from 'vuex'

import { storageNamespace } from '~/helpers/storage-namespaces'

import { formatAttraqtProduct } from '~/format/product-attraqt'

import { getCollection } from '~/core/get-collection'
import { getSearch } from '~/core/get-search'

/**
 * Format products received from Attraqt API.
 * @param {Array} items - Raw array of products received from Attraqt.
 * @return {Array}
 */
function formatProductResponse(items = []) {
  return items.map((product) => {
    if (Object.keys(product).includes('source')) {
      return product
    }

    try {
      return formatAttraqtProduct(product)

    } catch (error) {
      const productObject = JSON.parse(JSON.stringify(product))

      console.error(
        '[error-format-attraqt-product] An error occurred while attempting to format product data from Fredhopper. The affected product will be hidden as a result.\n',
        '\n\nSee the affected product\'s data object for more details:',
        productObject,
        error,
      )

      return false
    }
  }).filter(Boolean)
}

/**
 * Static variable, does not need to be added to state.
 * - Used for preserving 'scroll position' on 'Back' press.
 */
let cachedApiResponse = {}

/**
 * Create a new VueX store.
 */
const store = createStore({
  state: {
    collection: {},
    filtersApplied: false,
    filtersFacets: {},
    productItemsSkeletonFlag: true,
    products: [],
    redirect: {
      active: false,
      type: false,
      url: false,
    },
    sortedBy: false,
    totalItems: 0,
  },

  getters: {
    getFacetMaps(state) {
      return state.collection[0].facetmap
    },
    getFilteredOnValues(state) {
      return state.collection[0].facetmap[0].filter.map((item) => item['on'])
    },
    getTotalItems(state) {
      const itemSection = state.collection[0]['items-section']

      return itemSection == undefined
        ? 0
        : itemSection.results['total-items']
    },
  },
  actions: {

    /**
     * Store the latest API response from Attraqt in the cache.
     * - https://mint-velvet.atlassian.net/wiki/spaces/TECHNOLOGY/pages/105119745/Restore+Scroll+Position+on+browser+Back
     * @param {Object} data - payload data for caching
     * @param {Object} data.existingCache - Existing stored PLP cache
     * @param {Object} data.payload - Paginated API response from Attraqt
     */
    cacheScrollPosition({ commit, state }, payload) {
      cachedApiResponse.universes.universe = state.collection

      const data = {
        collection: JSON.parse(JSON.stringify(cachedApiResponse)),
        ...payload,
      }

      sessionStorage.setItem(storageNamespace.plp.scrollRestore, JSON.stringify(data))
    },

    /**
     * Query Attraqt API using a querystring built in `CollectionPageContainer`
     * - Store results in the state
     * @param {String} payload - JSON payload for Attraqt.
     * @returns {Object}
     */
    async fetchAttraqtPage({ commit, state }, payload) {
      let cache = false

      if (mint.flags.collections.enableRestoreScroll) {
        cache = JSON.parse(
          sessionStorage.getItem(storageNamespace.plp.scrollRestore),
        ) || {}

        const cachedResponse = cache?.collection || false

        /**
         * If a payload exists in the cache, restore from that.
         * - Used to restore users scroll position on 'Back' press.
         * - https://mint-velvet.atlassian.net/wiki/spaces/TECHNOLOGY/pages/105119745/Restore+Scroll+Position+on+browser+Back
         */
        if (cachedResponse) {
          commit('setNewPage', {
            res: cachedResponse,
          })

          return cachedResponse
        }
      }

      /**
       * Otherwise, proceed with a normal request.
       */
      const response = await fetch('/apps/attraqt/', {
        body: payload,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store',

      }).then(resp => resp.json())

      response.source = 'attraqt'

      /**
       * This converts the Vue Proxy object to a plain JS object.
       */
      const cleanResponse = JSON.parse(JSON.stringify(response))

      commit('setNewPage', {
        res: cleanResponse,
      })

      return cleanResponse
    },

    /**
     * Query Shopify API using a data object built in `CollectionPageContainer`
     * - Store results in the state
     * @param {String} payload - JSON payload for Shopify.
     * @returns {Object}
     */
    async fetchShopifyPage({ commit, state }, payload) {
      const {
        startCursor,
        handle,
        terms,
        perPage: first,
      } = payload

      /**
       * Query either a collection or search results.
       * - Defaults to search, as an empty search term is valid, but an empty
       *   collection handle is not.
       */
      const isCollection = handle || false

      const response = isCollection
        ? await getCollection({
          startCursor,
          handle,
          first,
        })
        : await getSearch({
          startCursor,
          terms,
          first,
        })

      /**
       * This converts the Vue Proxy object to a plain JS object.
       */
      const cleanResponse = JSON.parse(JSON.stringify(response))

      commit('setNewPage', {
        res: cleanResponse,
      })

      return cleanResponse
    },
    fetchFiltersApplied({ commit }, payload) {
      commit('setFiltersApplied', payload)
    },
    fetchSortedBy({ commit }, payload) {
      commit('setSortedBy', payload)
    },
  },
  mutations: {
    setTotalItems(state, payload) {
      state.totalItems = payload
    },

    setSortedBy(state, payload) {
      state.sortedBy = payload
    },

    setFiltersApplied(state, payload) {
      state.filtersApplied = payload
    },

    /**
     * Fetch a paginated query from Attraqt.
     * - Format the product response received
     * - If > Page 1, append new paginated results
     * @param {Object} collection - Attraqt API response data.
     */
    setNewPage(state, collection) {
      cachedApiResponse = collection.res

      /**
       * Check for redirects.
       * - A JSON of the Shopify page/collection (/pages/page.json)
       * - If a single product, `res.info.view === 'detail'` and user is
       *  redirected to the first/only product.
       * - When VueX is removed in favour of Pinia, ideally the state
       *   should return the entire URL instead of component handling it.
       */
      state.redirect.state = false
      state.redirect.type = false

      let handle = false

      if (cachedApiResponse.collection) {
        state.redirect.active = true
        state.redirect.type = 'collections'
        handle = cachedApiResponse.collection.handle
      }

      if (cachedApiResponse.page) {
        state.redirect.active = true
        state.redirect.type = 'pages'
        handle = cachedApiResponse.page.handle
      }

      if (cachedApiResponse?.info?.view === 'detail') {
        state.redirect.active = true
        state.redirect.type = 'products'
      }

      /**
       * Early return if page redirect.
       * - When VueX is removed in favour of Pinia, ideally the state
       *   should return the entire URL instead of component handling it.
       */
      if (state.redirect.active && state.redirect.type !== 'products') {
        state.products = []
        state.collection = []

        state.redirect.url = `${mint.shop.localisedShopUrl}${state.redirect.type}/${handle}`

        return
      }

      /**
       * Handle pagination.
       */
      const currentCollectionExists = !!Object.keys(state.collection).length
      let isNewQuery = false
      let queryString = false

      if (mint?.flags?.shopifyMerchandising?.enable) {
        isNewQuery = !collection.res?.pageInfo?.hasPreviousPage
      } else {
        if (collection.res?.info) {
          queryString = collection?.res?.info['query-string-httpencoded']
        }

        if (queryString) {
          const queryParams = new URLSearchParams(
            decodeURIComponent(queryString),
          )

          const startIndex = queryParams?.get('fh_start_index')

          isNewQuery = startIndex === '0'
        }
      }

      /**
       * Set the collection object if it does not exist.
       */
      if (!currentCollectionExists || isNewQuery) {
        if (mint?.flags?.shopifyMerchandising?.enable) {
          state.collection = collection.res
          state.products = collection.res.products
          return
        }

        state.collection = JSON.parse(JSON.stringify(collection.res.universes.universe))

        const itemSection = state.collection[0]['items-section'] || false

        if (itemSection) {
          state.products = formatProductResponse(itemSection.items.item)
        }

        /**
         * Get handle of product
         */
        if (state.redirect.type === 'products') {
          const handle = state.products[0].handle
          state.redirect.url = `${mint.shop.localisedShopUrl}${state.redirect.type}/${handle}`
        }

        return
      }

      /**
       * If going beyond the first page, append new paginated items to the array.
       */
      const currentItems = state.products

      if (mint?.flags?.shopifyMerchandising?.enable) {
        const newItems = collection.res.products

        state.products = [].concat(currentItems, newItems)
      } else {
        const newItems = formatProductResponse(
          collection.res.universes.universe[0]['items-section'].items.item,
        )

        state.collection[0]['items-section'].items.item =
          [].concat(currentItems, newItems)

        state.products = state.collection[0]['items-section'].items.item
      }
    },

    /**
     * Toggles the visibility of the initial loading skeleton.
     * @param {Boolean} flag - If to show the skeleton.
     */
    setProductItemsSkeletonFlag(state, flag) {
      state.productItemsSkeletonFlag = flag
    },
  },
})

export default store
